/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IAddressSelectItem, IAddressSelectProps } from '@msdyn365-commerce-modules/address';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address';
import { ICheckoutShippingAddressViewProps } from '@msdyn365-commerce-modules/address';
import GetProductEligibilityAction, { GetProductEligibilityInput } from '../../../actions/get-product-eligibility.action';
import { CommercePropertyValueEnum } from '../../../modules/allowable-freight/utils/CommercePropertyValueEnum';
import { CommerceProperty } from '@msdyn365-commerce/retail-proxy';
/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem) => {
                return <>{item.description}</>;
            })}
        </Node>
    );
};

/**
 * Address Select Component.
 * @param param0 - Root param.
 * @param param0.SelectAddress - Select address.
 * @param param0.addButton - Add button.
 * @param param0.items - IAddressSelectItem[].
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - SelectAddress Node.
 */
const AddressSelect: React.FC<IAddressSelectProps> = ({
    SelectAddress,
    addButton,
    items,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...SelectAddress}>
            {addButton}
            {items.map((item: IAddressSelectItem) => {
                const SelectItem = item.SelectItem;
                return (
                    <Node {...SelectItem} key={item.key}>
                        {item.input}
                        <AddressShow {...item.showItems} />
                    </Node>
                );
            })}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                return (
                    <Node {...AddressItem} key={key}>
                        {label}
                        {alert}
                        {input}
                    </Node>
                );
            })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Checkout Shipping Address View Component.
 * @param props - Props.
 * @returns - CheckoutShippingAddress Module.
 */
const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewProps> = props => {
    const { CheckoutShippingAddress, viewState, showAddress, showAddressSelect, showAddOrUpdateAddress, cartLineImages } = props;
    const address = props.data.checkout.result?.shippingAddress;
    const [nonEligibleItems, setnonEligibleItems] = React.useState<string[]>([]);
    React.useEffect(() => {
        void checkEligibilityForShippingAddress();
    }, [address]);
    React.useEffect(() => {
        props.moduleState.setHasError(true);
    }, []);

    const _getExtensionPropertyValueByKey = (
        properties: CommerceProperty[] | undefined,
        key: string,
        valueType: CommercePropertyValueEnum
    ) => {
        const property = properties?.find(p => p.Key === key)?.Value;

        // eslint-disable-next-line security/detect-object-injection
        const val = property ? property[valueType] : null;
        return val;
    };

    const checkEligibilityForShippingAddress = async () => {
        const notEligibleList: string[] = [];
        const cart = props.data.checkout.result?.checkoutCart?.cart;
        if (cart && address) {
            const itemIds = cart.CartLines?.map(cl => cl.ItemId!) || [];
            const addressCountry = address.ThreeLetterISORegionName || null;
            const addressState = address.State || null;
            const addressRegion = address.County || null;
            const input = new GetProductEligibilityInput(itemIds, true, cart.Id || '', addressCountry, addressState, addressRegion);
            const eligibilityData = await GetProductEligibilityAction(input, props.context.actionContext);
            if (eligibilityData.length) {
                eligibilityData.forEach((i: any) => {
                    const id = _getExtensionPropertyValueByKey(i.ExtensionProperties, 'ItemId', CommercePropertyValueEnum.string);
                    if (!i.IsEligible) {
                        notEligibleList.push(id as string);
                    }
                });
            }
        }
        setnonEligibleItems(notEligibleList);
        props.moduleState.setHasError(notEligibleList.length > 0);
    };

    const renderCartError = () => {
        if (!nonEligibleItems.length) {
            return null;
        }
        return (
            <div>
                <p className='ms-checkout__error-message' role='alert' aria-live='assertive'>
                    The following items are not eligible for your current shipping address. Please remove them from the cart or change your
                    address:
                </p>
                {nonEligibleItems.map(i => {
                    const item = getCartLineFromItemId(i);
                    return (
                        <p>
                            {item?.ItemId} - {item?.Description}
                        </p>
                    );
                })}
                <button className={'ms-checkout__guided-card-btn-save msc-btn btn-primary'} onClick={removeIneligibleItems}>
                    Remove Items
                </button>
            </div>
        );
    };

    const removeIneligibleItems = async () => {
        const cartState = props.data.cart.result;
        const checkoutState = props.data.checkout.result;
        const badCartLines: string[] = [];
        cartState!.cart.CartLines!.forEach(cl => {
            if (nonEligibleItems.includes(cl.ItemId!)) {
                badCartLines.push(cl.LineId!);
            }
        });
        await checkoutState!.checkoutCart.removeCartLines({ cartLineIds: badCartLines });
        await cartState!.removeCartLines({ cartLineIds: badCartLines });
        window.location.reload();
    };

    const getCartLineFromItemId = (itemId: string) =>
        props.data.checkout.result?.checkoutCart.cart.CartLines?.find(i => i.ItemId === itemId);
    return (
        <Module {...CheckoutShippingAddress}>
            {cartLineImages}
            {viewState.isShowAddress && <AddressShow {...showAddress} />}
            {viewState.isShowAddresList && <AddressSelect {...showAddressSelect} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress} />}
            {renderCartError()}
        </Module>
    );
};

export default CheckoutShippingAddressView;
